import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageCircle, ArrowRight, CheckCircle2, Menu, X } from "lucide-react";
import axios from "axios";
import { toast, Toaster } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ease = [0.25, 1, 0.5, 1];

const NAV_LINKS = [
  { label: "Work", href: "#work" },
  { label: "Services", href: "#services" },
  { label: "Estimate", href: "#estimate" },
  { label: "Contact", href: "#contact" },
];

const SERVICES = [
  {
    name: "Sod Installation",
    blurb: "Premium turfgrass laid roll-by-roll for an instant, healthy, showroom lawn.",
    accent: "from-[#2E7D32] to-[#1B3A1B]",
    img: "/services/sod.jpg",
    link: "/sod-installation",
  },
  {
    name: "Artificial Turf Installation",
    blurb: "Year-round green without the upkeep — clean lines, soft feel, zero mowing.",
    accent: "from-[#1F70BF] to-[#1B3A1B]",
    img: "/services/turf.jpg",
  },
  {
    name: "Lawn Maintenance",
    blurb: "Weekly care to keep your turf dense, green, and showroom-ready year-round.",
    accent: "from-[#388E3C] to-[#1B3A1B]",
    img: "/services/lawn.png",
  },
  {
    name: "Drainage Solutions",
    blurb: "French drains, regrading, and water management to keep your yard dry and protected.",
    accent: "from-[#1F70BF] to-[#1B3A1B]",
    img: "/services/drainage.jpg",
  },
  {
    name: "Hardscaping",
    blurb: "Pavers, walkways, and retaining walls — built to last and built to impress.",
    accent: "from-[#8B5A2B] to-[#1B3A1B]",
    img: "/services/hardscaping.jpg",
  },
  {
    name: "Junk & Debris Removal",
    blurb: "Clear-outs, haul-aways, and post-project cleanup so your property is left spotless.",
    accent: "from-[#388E3C] to-[#1B3A1B]",
    img: "/services/junk.jpg",
  },
  {
    name: "Concrete",
    blurb: "Driveways, patios, walkways, and slabs — poured clean, cured strong, and built to last.",
    accent: "from-[#6B6B6B] to-[#1B3A1B]",
    img: "/services/concrete.webp",
    link: "/concrete",
  },
];

const STATS = [
  { k: "250+", v: "Projects delivered" },
  { k: "12 yrs", v: "Serving Birmingham" },
  { k: "4.9 / 5", v: "Client satisfaction" },
];

const TESTIMONIALS = [
  {
    quote:
      "Yair's team came out and did some major clean up after Chinese Privet overgrowth started taking over a portion of the front yard. They made it look great! In addition, they mowed, edged, & weed eat (trim) the grass. I will definitely use them again in the near future.",
    name: "Jim Milburn",
    role: "McCalla",
    rating: 5,
  },
  {
    quote:
      "Had a great experience! We didn't have time to take care of the yard after the storms and our bushes were a bit overgrown so we called up these guys and they took care of everything! The price was exactly what was quoted and they made our yard look great! Would recommend to anyone!",
    name: "Keelon Ellis",
    role: "Birmingham",
    rating: 5,
  },
  {
    quote:
      "These guys are great! Did a beautiful job at a reasonable price. Very courteous and polite got the job done with no hitches. Highly recommended, a rare find now a days.",
    name: "Michael Willingham",
    role: "Helena",
    rating: 5,
  },
];

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F4F8F2]/90 backdrop-blur-xl border-b border-[#D1CFC7]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-3">
          <img
            src="/brand/yair-logo.jpg"
            alt="Yair Landscaping"
            className="h-14 w-auto rounded-md object-contain"
          />
          <span
            className={`font-serif text-2xl tracking-tight ${
              scrolled ? "text-[#122412]" : "text-[#F4F8F2]"
            }`}
          >
            Yair <span className="italic font-light">Landscaping</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`overline hover:text-[#1F70BF] transition-colors ${
                scrolled ? "text-[#122412]" : "text-[#F4F8F2]"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <a
          href="#contact"
          data-testid="nav-cta-quote"
          className={`hidden md:inline-flex items-center gap-2 overline px-6 py-3 border transition-colors ${
            scrolled
              ? "border-[#2E7D32] text-[#2E7D32] hover:bg-[#2E7D32] hover:text-[#F4F8F2]"
              : "border-[#F4F8F2] text-[#F4F8F2] hover:bg-[#F4F8F2] hover:text-[#122412]"
          }`}
        >
          Free Quote <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
        </a>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className={`md:hidden p-2 ${scrolled ? "text-[#122412]" : "text-[#F4F8F2]"}`}
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#F4F8F2] border-t border-[#D1CFC7]"
          >
            <div className="flex flex-col p-6 gap-5">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="overline text-[#122412]"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-cta"
                className="overline bg-[#2E7D32] text-[#F4F8F2] px-6 py-4 text-center"
              >
                Get A Free Quote
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full overflow-hidden bg-[#1B3A1B]"
    >
      <img
        src="/hero/team.jpg"
        alt="Yair Landscaping crew at work"
        className="absolute inset-0 w-full h-full object-cover opacity-70"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1B3A1B] via-[#1B3A1B]/50 to-[#1B3A1B]/30" />
      <div className="grain absolute inset-0" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 min-h-[100vh] flex flex-col justify-end pb-20 lg:pb-28 pt-32">
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease, delay: 0.15 }}
          className="font-serif text-5xl sm:text-7xl lg:text-[8rem] text-[#F4F8F2] font-light leading-[0.95] tracking-tight max-w-5xl"
        >
          Outdoor spaces,
          <br />
          <span className="italic text-[#DDECD7]">quietly extraordinary.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.4 }}
          className="font-body mt-10 text-[#D1CFC7] max-w-xl text-lg leading-relaxed"
        >
          Design, build, and care for gardens that grow more beautiful with every
          season — crafted for discerning homes across Greater Birmingham.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.55 }}
          className="mt-12 flex flex-col sm:flex-row gap-4"
        >
          <a
            href="#contact"
            data-testid="hero-cta-quote"
            className="group inline-flex items-center justify-center gap-3 bg-[#F4F8F2] text-[#122412] px-10 py-5 overline hover:bg-[#1F70BF] hover:text-white transition-colors"
          >
            Get a Free Quote
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
          </a>
          <a
            href="tel:+12052033712"
            data-testid="hero-cta-call"
            className="inline-flex items-center justify-center gap-3 border border-[#F4F8F2]/60 text-[#F4F8F2] px-10 py-5 overline hover:bg-[#F4F8F2] hover:text-[#122412] transition-colors"
          >
            <Phone className="w-4 h-4" strokeWidth={1.5} /> (205) 203-3712
          </a>
        </motion.div>
      </div>
    </section>
  );
}

function Stats() {
  return (
    <section data-testid="stats-section" className="bg-[#1B3A1B] py-10 border-y border-[#2E7D32]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-0">
        {STATS.map((s, i) => (
          <motion.div
            key={s.v}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease, delay: i * 0.1 }}
            className={`flex items-baseline gap-6 px-2 md:px-10 ${
              i > 0 ? "md:border-l md:border-[#2E7D32]" : ""
            }`}
          >
            <span className="font-serif text-5xl md:text-6xl text-[#F4F8F2] font-light">{s.k}</span>
            <span className="overline text-[#1F70BF]">{s.v}</span>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function Services() {
  return (
    <section id="services" data-testid="services-section" className="relative py-24 lg:py-32 bg-[#F4F8F2]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-16">
          <div className="overline text-[#1F70BF] mb-6">What we do</div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#122412] font-light leading-[1.05] tracking-tight max-w-3xl">
            Every discipline,
            <br />
            <span className="italic">one exacting standard.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => {
            const CardInner = (
              <>
              <div className={`relative aspect-[16/9] overflow-hidden bg-gradient-to-br ${s.accent}`}>
                {s.img && (
                  <img
                    src={s.img}
                    alt={s.name}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#1B3A1B]/80 via-[#1B3A1B]/20 to-transparent" />
                <div className="grain absolute inset-0 opacity-40" />
                <div className="absolute top-5 left-5 overline text-[#F4F8F2]/90">0{i + 1}</div>
                <div className="absolute bottom-5 left-5 right-5">
                  <h3 className="font-serif text-3xl md:text-4xl text-[#F4F8F2] font-light tracking-tight leading-none">
                    {s.name}
                  </h3>
                </div>
              </div>
              <div className="p-6 md:p-8">
                <p className="font-body text-[#4A6B4A] leading-relaxed">{s.blurb}</p>
                {s.link ? (
                  <Link
                    to={s.link}
                    data-testid={`service-cta-${i}`}
                    className="inline-flex items-center gap-2 mt-6 overline text-[#2E7D32] hover:text-[#1F70BF] transition-colors"
                  >
                    Learn More <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </Link>
                ) : (
                  <a
                    href="#contact"
                    data-testid={`service-cta-${i}`}
                    className="inline-flex items-center gap-2 mt-6 overline text-[#2E7D32] hover:text-[#1F70BF] transition-colors"
                  >
                    Request <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </a>
                )}
              </div>
              </>
            );
            return (
              <motion.article
                key={s.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.7, ease, delay: i * 0.08 }}
                data-testid={`service-card-${i}`}
                className="group relative overflow-hidden bg-[#F4F8F2] border border-[#C5D8B8] hover:border-[#2E7D32] transition-colors"
              >
                {s.link ? (
                  <Link to={s.link} className="block" data-testid={`service-card-link-${i}`}>
                    {CardInner}
                  </Link>
                ) : (
                  CardInner
                )}
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function BeforeAfter() {
  const slides = ["/work/ba1.jpg", "/work/ba2.png", "/work/ba3.jpg", "/work/ba4.jpg"];
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % slides.length), 3000);
    return () => clearInterval(id);
  }, [slides.length]);

  return (
    <section id="work" data-testid="work-section" className="py-24 lg:py-32 bg-[#DDECD7] relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-12 mb-16 items-end gap-8">
          <div className="md:col-span-7">
            <div className="overline text-[#1F70BF] mb-6">Recent transformations</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#122412] font-light leading-[1.05] tracking-tight">
              Before. After.
              <br />
              <span className="italic">Always personal.</span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="font-body text-[#4A6B4A] text-lg leading-relaxed">
              A selection of recent projects completed by the Yair Landscaping team.
            </p>
          </div>
        </div>

        <div
          data-testid="before-after-fade"
          className="relative w-full aspect-[4/5] sm:aspect-square md:aspect-[16/10] overflow-hidden border border-[#D1CFC7] bg-[#1B3A1B]"
        >
          {slides.map((src, i) => (
            <img
              key={src}
              src={src}
              alt={`Project ${i + 1}`}
              className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-1000 ease-in-out ${
                idx === i ? "opacity-100" : "opacity-0"
              }`}
            />
          ))}

          {/* Slide indicator dots */}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                data-testid={`ba-dot-${i}`}
                aria-label={`View transformation ${i + 1}`}
                className={`h-1.5 transition-all ${
                  idx === i ? "w-10 bg-[#F4F8F2]" : "w-5 bg-[#F4F8F2]/40 hover:bg-[#F4F8F2]/70"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Estimate() {
  const services = ["Pine Straw Installation", "Mulch Installation", "Synthetic Turf", "Pressure Washing"];
  const [service, setService] = useState("Pine Straw Installation");
  const [rolls, setRolls] = useState(10);
  const [yards, setYards] = useState(3);
  const [sqft, setSqft] = useState(500);
  const [propertyType, setPropertyType] = useState("residential");
  const [washSqft, setWashSqft] = useState(1000);
  const [washType, setWashType] = useState("standard");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const isPineStraw = service === "Pine Straw Installation";
  const isMulch = service === "Mulch Installation";
  const isTurf = service === "Synthetic Turf";
  const isWash = service === "Pressure Washing";

  const calc = async () => {
    setLoading(true);
    try {
      const payload = { service };
      if (isPineStraw) payload.rolls = rolls;
      if (isMulch) payload.yards = yards;
      if (isTurf) {
        payload.sqft = sqft;
        payload.property_type = propertyType;
      }
      if (isWash) {
        payload.sqft = washSqft;
        payload.wash_type = washType;
      }
      const { data } = await axios.post(`${API}/estimate`, payload);
      setResult(data);
    } catch (err) {
      toast.error("Could not fetch estimate. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="estimate" data-testid="estimate-section" className="py-24 lg:py-32 bg-[#1B3A1B] relative overflow-hidden">
      <div className="grain absolute inset-0" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="overline text-[#1F70BF] mb-6">Instant estimate</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F4F8F2] font-light leading-[1.05] tracking-tight">
              A starting number,
              <br />
              <span className="italic text-[#D1CFC7]">in two clicks.</span>
            </h2>
            <p className="font-body text-[#D1CFC7]/80 mt-8 leading-relaxed">
              Pick a service — answer a quick question or two, and we'll return a
              realistic starting price. Every estimate is dialed in with a
              complimentary on-site visit.
            </p>
          </div>

          <div className="md:col-span-7 bg-[#F4F8F2] p-8 md:p-12 relative">
            <div className="overline text-[#1F70BF] mb-4">01 — Service</div>
            <div className="flex flex-wrap gap-2 mb-10">
              {services.map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    setService(s);
                    setResult(null);
                  }}
                  data-testid={`estimate-service-${s.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`overline px-5 py-3 border transition-colors ${
                    service === s
                      ? "bg-[#2E7D32] text-[#F4F8F2] border-[#2E7D32]"
                      : "bg-transparent text-[#122412] border-[#D1CFC7] hover:border-[#2E7D32]"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>

            {isPineStraw && (
              <div className="mb-10" data-testid="pine-straw-rolls-panel">
                <div className="overline text-[#1F70BF] mb-4">02 — How many rolls?</div>
                <div className="flex items-center gap-4 mb-4">
                  <button
                    type="button"
                    data-testid="rolls-decrement"
                    onClick={() => {
                      setRolls((r) => Math.max(1, r - 1));
                      setResult(null);
                    }}
                    className="w-12 h-12 border border-[#D1CFC7] text-[#122412] text-xl hover:border-[#2E7D32] transition-colors"
                    aria-label="Decrease rolls"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="1"
                    max="2000"
                    value={rolls}
                    data-testid="rolls-input"
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setRolls(isNaN(v) ? 1 : Math.max(1, Math.min(2000, v)));
                      setResult(null);
                    }}
                    className="w-24 text-center font-serif text-3xl text-[#122412] border border-[#D1CFC7] py-2 focus:outline-none focus:border-[#1F70BF]"
                  />
                  <button
                    type="button"
                    data-testid="rolls-increment"
                    onClick={() => {
                      setRolls((r) => Math.min(2000, r + 1));
                      setResult(null);
                    }}
                    className="w-12 h-12 border border-[#D1CFC7] text-[#122412] text-xl hover:border-[#2E7D32] transition-colors"
                    aria-label="Increase rolls"
                  >
                    +
                  </button>
                  <span className="font-body text-[#4A6B4A] text-sm ml-2">
                    × $24 / roll installed
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[10, 25, 50, 100].map((q) => (
                    <button
                      key={q}
                      type="button"
                      data-testid={`rolls-preset-${q}`}
                      onClick={() => {
                        setRolls(q);
                        setResult(null);
                      }}
                      className={`overline px-4 py-2 border transition-colors ${
                        rolls === q
                          ? "bg-[#1F70BF] text-white border-[#1F70BF]"
                          : "bg-transparent text-[#122412] border-[#D1CFC7] hover:border-[#1F70BF]"
                      }`}
                    >
                      {q} rolls
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isMulch && (
              <div className="mb-10" data-testid="mulch-yards-panel">
                <div className="overline text-[#1F70BF] mb-4">02 — How many yards?</div>
                <div className="flex items-center gap-4 mb-4">
                  <button
                    type="button"
                    data-testid="yards-decrement"
                    onClick={() => {
                      setYards((y) => Math.max(1, y - 1));
                      setResult(null);
                    }}
                    className="w-12 h-12 border border-[#C5D8B8] text-[#122412] text-xl hover:border-[#2E7D32] transition-colors"
                    aria-label="Decrease yards"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="1"
                    max="2000"
                    value={yards}
                    data-testid="yards-input"
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setYards(isNaN(v) ? 1 : Math.max(1, Math.min(2000, v)));
                      setResult(null);
                    }}
                    className="w-24 text-center font-serif text-3xl text-[#122412] border border-[#C5D8B8] py-2 focus:outline-none focus:border-[#1F70BF]"
                  />
                  <button
                    type="button"
                    data-testid="yards-increment"
                    onClick={() => {
                      setYards((y) => Math.min(2000, y + 1));
                      setResult(null);
                    }}
                    className="w-12 h-12 border border-[#C5D8B8] text-[#122412] text-xl hover:border-[#2E7D32] transition-colors"
                    aria-label="Increase yards"
                  >
                    +
                  </button>
                  <span className="font-body text-[#4A6B4A] text-sm ml-2">
                    × $115 / yard installed
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[3, 5, 10, 20].map((q) => (
                    <button
                      key={q}
                      type="button"
                      data-testid={`yards-preset-${q}`}
                      onClick={() => {
                        setYards(q);
                        setResult(null);
                      }}
                      className={`overline px-4 py-2 border transition-colors ${
                        yards === q
                          ? "bg-[#1F70BF] text-white border-[#1F70BF]"
                          : "bg-transparent text-[#122412] border-[#C5D8B8] hover:border-[#1F70BF]"
                      }`}
                    >
                      {q} yards
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isTurf && (
              <div className="mb-10" data-testid="turf-panel">
                <div className="overline text-[#1F70BF] mb-4">02 — Property type</div>
                <div className="flex flex-wrap gap-2 mb-8">
                  {[
                    { k: "residential", v: "Residential · $14–$16 / sq ft" },
                    { k: "commercial", v: "Commercial · $20 / sq ft" },
                  ].map((p) => (
                    <button
                      key={p.k}
                      type="button"
                      data-testid={`turf-property-${p.k}`}
                      onClick={() => {
                        setPropertyType(p.k);
                        setResult(null);
                      }}
                      className={`overline px-5 py-3 border transition-colors ${
                        propertyType === p.k
                          ? "bg-[#1F70BF] text-white border-[#1F70BF]"
                          : "bg-transparent text-[#122412] border-[#D1CFC7] hover:border-[#1F70BF]"
                      }`}
                    >
                      {p.v}
                    </button>
                  ))}
                </div>

                <div className="overline text-[#1F70BF] mb-4">03 — Square footage</div>
                <div className="flex items-center gap-4 mb-4 flex-wrap">
                  <button
                    type="button"
                    data-testid="sqft-decrement"
                    onClick={() => {
                      setSqft((s) => Math.max(50, s - 50));
                      setResult(null);
                    }}
                    className="w-12 h-12 border border-[#D1CFC7] text-[#122412] text-xl hover:border-[#2E7D32] transition-colors"
                    aria-label="Decrease sqft"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="1"
                    max="1000000"
                    value={sqft}
                    data-testid="sqft-input"
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setSqft(isNaN(v) ? 1 : Math.max(1, Math.min(1000000, v)));
                      setResult(null);
                    }}
                    className="w-32 text-center font-serif text-3xl text-[#122412] border border-[#D1CFC7] py-2 focus:outline-none focus:border-[#1F70BF]"
                  />
                  <button
                    type="button"
                    data-testid="sqft-increment"
                    onClick={() => {
                      setSqft((s) => Math.min(1000000, s + 50));
                      setResult(null);
                    }}
                    className="w-12 h-12 border border-[#D1CFC7] text-[#122412] text-xl hover:border-[#2E7D32] transition-colors"
                    aria-label="Increase sqft"
                  >
                    +
                  </button>
                  <span className="font-body text-[#4A6B4A] text-sm">sq ft</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[250, 500, 1000, 2500].map((q) => (
                    <button
                      key={q}
                      type="button"
                      data-testid={`sqft-preset-${q}`}
                      onClick={() => {
                        setSqft(q);
                        setResult(null);
                      }}
                      className={`overline px-4 py-2 border transition-colors ${
                        sqft === q
                          ? "bg-[#1F70BF] text-white border-[#1F70BF]"
                          : "bg-transparent text-[#122412] border-[#D1CFC7] hover:border-[#1F70BF]"
                      }`}
                    >
                      {q.toLocaleString()} sq ft
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isWash && (
              <div className="mb-10" data-testid="wash-panel">
                <div className="overline text-[#1F70BF] mb-4">02 — Wash type</div>
                <div className="flex flex-wrap gap-2 mb-8">
                  {[
                    { k: "standard", v: "Standard · $0.15–$0.30 / sq ft" },
                    { k: "chemical", v: "Chemical · $0.30–$0.60 / sq ft" },
                  ].map((w) => (
                    <button
                      key={w.k}
                      type="button"
                      data-testid={`wash-type-${w.k}`}
                      onClick={() => {
                        setWashType(w.k);
                        setResult(null);
                      }}
                      className={`overline px-5 py-3 border transition-colors ${
                        washType === w.k
                          ? "bg-[#1F70BF] text-white border-[#1F70BF]"
                          : "bg-transparent text-[#122412] border-[#D1CFC7] hover:border-[#1F70BF]"
                      }`}
                    >
                      {w.v}
                    </button>
                  ))}
                </div>

                <div className="overline text-[#1F70BF] mb-4">03 — Square footage</div>
                <div className="flex items-center gap-4 mb-4 flex-wrap">
                  <button
                    type="button"
                    data-testid="wash-sqft-decrement"
                    onClick={() => {
                      setWashSqft((s) => Math.max(50, s - 100));
                      setResult(null);
                    }}
                    className="w-12 h-12 border border-[#D1CFC7] text-[#122412] text-xl hover:border-[#2E7D32] transition-colors"
                    aria-label="Decrease sqft"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="1"
                    max="1000000"
                    value={washSqft}
                    data-testid="wash-sqft-input"
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setWashSqft(isNaN(v) ? 1 : Math.max(1, Math.min(1000000, v)));
                      setResult(null);
                    }}
                    className="w-32 text-center font-serif text-3xl text-[#122412] border border-[#D1CFC7] py-2 focus:outline-none focus:border-[#1F70BF]"
                  />
                  <button
                    type="button"
                    data-testid="wash-sqft-increment"
                    onClick={() => {
                      setWashSqft((s) => Math.min(1000000, s + 100));
                      setResult(null);
                    }}
                    className="w-12 h-12 border border-[#D1CFC7] text-[#122412] text-xl hover:border-[#2E7D32] transition-colors"
                    aria-label="Increase sqft"
                  >
                    +
                  </button>
                  <span className="font-body text-[#4A6B4A] text-sm">sq ft</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[500, 1000, 2500, 5000].map((q) => (
                    <button
                      key={q}
                      type="button"
                      data-testid={`wash-sqft-preset-${q}`}
                      onClick={() => {
                        setWashSqft(q);
                        setResult(null);
                      }}
                      className={`overline px-4 py-2 border transition-colors ${
                        washSqft === q
                          ? "bg-[#1F70BF] text-white border-[#1F70BF]"
                          : "bg-transparent text-[#122412] border-[#D1CFC7] hover:border-[#1F70BF]"
                      }`}
                    >
                      {q.toLocaleString()} sq ft
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={calc}
              disabled={loading}
              data-testid="estimate-calculate-btn"
              className="w-full sm:w-auto overline inline-flex items-center gap-3 bg-[#2E7D32] text-[#F4F8F2] px-10 py-5 hover:bg-[#388E3C] transition-colors disabled:opacity-60"
            >
              {loading ? "Calculating…" : "Calculate Estimate"}
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>

            <AnimatePresence>
              {result && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="mt-10 pt-10 border-t border-[#D1CFC7]"
                  data-testid="estimate-result"
                >
                  <div className="overline text-[#1F70BF] mb-3">Your estimate</div>
                  <div className="flex items-baseline gap-4 flex-wrap">
                    <div className="font-serif text-5xl md:text-6xl text-[#122412] font-light">
                      {result.display}
                    </div>
                    <div className="font-body text-[#4A6B4A] text-sm">
                      {result.service}
                      {result.rolls ? ` · ${result.rolls} rolls` : ""}
                      {result.yards ? ` · ${result.yards} yards` : ""}
                      {result.sqft && result.property_type ? ` · ${result.sqft.toLocaleString()} sq ft · ${result.property_type}` : ""}
                      {result.sqft && result.wash_type ? ` · ${result.sqft.toLocaleString()} sq ft · ${result.wash_type} wash` : ""}
                    </div>
                  </div>
                  <p className="font-body text-[#4A6B4A] mt-4 leading-relaxed text-sm">
                    {result.note}
                  </p>
                  <a
                    href="#contact"
                    data-testid="estimate-to-form-cta"
                    className="inline-flex items-center gap-2 mt-6 overline text-[#2E7D32] hover:text-[#1F70BF]"
                  >
                    Lock this in — get a formal quote <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section data-testid="testimonials-section" className="py-24 lg:py-32 bg-[#F4F8F2]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-16">
          <div className="md:col-span-8">
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#122412] font-light leading-[1.05] tracking-tight">
              What people think
              <br />
              <span className="italic">of our work.</span>
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <a
              href="https://g.page/r/CVA2Emc0-gcSEAE/review"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.preventDefault();
                const url = "https://g.page/r/CVA2Emc0-gcSEAE/review";
                const win = window.open(url, "_blank", "noopener,noreferrer");
                if (!win || win.closed || typeof win.closed === "undefined") {
                  window.top.location.href = url;
                }
              }}
              data-testid="leave-review-btn"
              className="inline-flex items-center justify-center gap-3 bg-[#2E7D32] text-[#F4F8F2] px-8 py-4 overline hover:bg-[#388E3C] transition-colors"
            >
              Give Us A Rating
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease, delay: i * 0.1 }}
              data-testid={`testimonial-${i}`}
              className="bg-[#DDECD7] p-8 md:p-10 flex flex-col justify-between min-h-[280px]"
            >
              <div>
                <div className="flex gap-0.5 mb-5" data-testid={`testimonial-stars-${i}`}>
                  {Array.from({ length: t.rating || 5 }).map((_, k) => (
                    <svg key={k} className="w-4 h-4 text-[#1F70BF]" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.363 1.118l1.286 3.957c.3.921-.755 1.688-1.54 1.118L10.587 15.5a1 1 0 00-1.176 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.286-3.957a1 1 0 00-.363-1.118L2.05 9.394c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z" />
                    </svg>
                  ))}
                </div>
                <blockquote className="font-serif text-lg md:text-xl text-[#122412] font-light leading-snug">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
              </div>
              <div className="mt-8 flex items-center gap-4">
                <div className="w-11 h-11 rounded-full bg-[#2E7D32] text-[#F4F8F2] flex items-center justify-center font-display font-medium">
                  {t.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                </div>
                <div>
                  <div className="font-display text-sm font-medium text-[#122412]">{t.name}</div>
                  <div className="overline text-[#4A6B4A]">{t.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LeadForm() {
  const [form, setForm] = useState({ name: "", phone: "", service: "Pine Straw Installation", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSubmitted(true);
      toast.success("Request received — we'll reach out within 1–2 hours.");
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32 bg-[#DDECD7] relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="overline text-[#1F70BF] mb-6">Free consultation</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#122412] font-light leading-[1.05] tracking-tight">
              Tell us about
              <br />
              <span className="italic">the space.</span>
            </h2>
            <p className="font-body text-[#4A6B4A] mt-8 leading-relaxed max-w-md">
              Most quotes are returned within 1–2 hours. No pressure, no obligation —
              just a thoughtful plan tailored to your property.
            </p>

            <div className="mt-12 space-y-5">
              <div className="flex items-center gap-4">
                <Phone className="w-5 h-5 text-[#1F70BF]" strokeWidth={1.5} />
                <a href="tel:+12052033712" className="font-body text-[#122412] hover:text-[#1F70BF]" data-testid="contact-phone">
                  (205) 203-3712
                </a>
              </div>
              <div className="flex items-center gap-4">
                <MessageCircle className="w-5 h-5 text-[#1F70BF]" strokeWidth={1.5} />
                <a href="sms:+12052033712" className="font-body text-[#122412] hover:text-[#1F70BF]" data-testid="contact-sms">
                  Text us instantly
                </a>
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            <div className="bg-[#F4F8F2] p-8 md:p-12 border border-[#D1CFC7]">
              {submitted ? (
                <div data-testid="form-success" className="py-10 text-center">
                  <CheckCircle2 className="w-14 h-14 text-[#1F70BF] mx-auto mb-6" strokeWidth={1.2} />
                  <h3 className="font-serif text-3xl md:text-4xl text-[#122412] font-light mb-3">
                    You're all set.
                  </h3>
                  <p className="font-body text-[#4A6B4A] max-w-md mx-auto">
                    We received your request and will reach out shortly with next steps.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setForm({ name: "", phone: "", service: "Pine Straw Installation", message: "" });
                    }}
                    data-testid="form-reset-btn"
                    className="overline mt-8 text-[#2E7D32] hover:text-[#1F70BF]"
                  >
                    Submit another →
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-8" data-testid="lead-form">
                  <div>
                    <label className="overline text-[#4A6B4A] mb-2 block" htmlFor="name">Name</label>
                    <input
                      id="name"
                      name="name"
                      value={form.name}
                      onChange={onChange}
                      data-testid="form-name"
                      required
                      placeholder="Jane Doe"
                      className="w-full bg-transparent border-b border-[#122412]/20 py-3 font-body text-[#122412] placeholder:text-[#4A6B4A]/50 focus:outline-none focus:border-[#1F70BF] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="overline text-[#4A6B4A] mb-2 block" htmlFor="phone">Phone</label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={onChange}
                      data-testid="form-phone"
                      required
                      placeholder="(205) 555-0000"
                      className="w-full bg-transparent border-b border-[#122412]/20 py-3 font-body text-[#122412] placeholder:text-[#4A6B4A]/50 focus:outline-none focus:border-[#1F70BF] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="overline text-[#4A6B4A] mb-2 block" htmlFor="service">Service</label>
                    <select
                      id="service"
                      name="service"
                      value={form.service}
                      onChange={onChange}
                      data-testid="form-service"
                      className="w-full bg-transparent border-b border-[#122412]/20 py-3 font-body text-[#122412] focus:outline-none focus:border-[#1F70BF] transition-colors"
                    >
                      <option>Pine Straw Installation</option>
                      <option>Synthetic Turf</option>
                      <option>Sod Installation</option>
                      <option>Pressure Washing</option>
                      <option>Lawn Maintenance</option>
                      <option>Hardscaping</option>
                      <option>Concrete</option>
                      <option>Drainage Solutions</option>
                      <option>Junk & Debris Removal</option>
                      <option>Cleanups</option>
                      <option>Planting</option>
                      <option>Gutter Cleaning</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="overline text-[#4A6B4A] mb-2 block" htmlFor="message">Tell us more (optional)</label>
                    <textarea
                      id="message"
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      data-testid="form-message"
                      rows={3}
                      placeholder="Timeline, inspiration, specific goals…"
                      className="w-full bg-transparent border-b border-[#122412]/20 py-3 font-body text-[#122412] placeholder:text-[#4A6B4A]/50 focus:outline-none focus:border-[#1F70BF] transition-colors resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="form-submit"
                    className="w-full overline bg-[#2E7D32] text-[#F4F8F2] px-8 py-5 hover:bg-[#388E3C] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-3"
                  >
                    {loading ? "Sending…" : "Request Quote"}
                    <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  <p className="font-body text-[#4A6B4A] text-xs text-center">
                    No pressure. No obligation.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[#1B3A1B] text-[#D1CFC7]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-5">
            <div className="mb-6">
              <img
                src="/brand/yair-logo.jpg"
                alt="Yair Landscaping"
                className="h-24 w-auto rounded-md"
              />
            </div>
            <p className="font-body max-w-sm leading-relaxed">
              Landscape design, build, and care for discerning homes across
              Birmingham, Alabama.
            </p>
          </div>
          <div className="md:col-span-3">
            <div className="overline text-[#1F70BF] mb-5">Reach</div>
            <ul className="font-body space-y-3">
              <li>(205) 203-3712</li>
              <li>Yairlandscaping1@gmail.com</li>
            </ul>
          </div>
          <div className="md:col-span-4">
            <div className="overline text-[#1F70BF] mb-5">Hours</div>
            <ul className="font-body space-y-3">
              <li>Mon – Sat · 6:00 AM – 5:00 PM</li>
            </ul>
          </div>
        </div>
        <div className="hairline" />
        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="overline">© {new Date().getFullYear()} Yair Landscaping — Licensed & Insured</span>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <Nav />
      <Hero />
      <Services />
      <BeforeAfter />
      <Estimate />
      <Testimonials />
      <LeadForm />
      <Footer />
    </div>
  );
}
